import axios from "@services/axios";
import { AxiosResponse, ResponseType } from "axios";

import { APIMethods } from "@typeDefinitions/enums";
import { API_URL } from "@config/app";

const WORD_PATTERN =
  /[A-Z]?[a-z]+(?=[A-Z]|[^A-Za-z0-9]|$)|[A-Z]+(?=[A-Z][a-z]|[^A-Za-z0-9]|$)|[A-Z]?[a-z]+|[A-Z]+|[0-9]+/g;

const splitWords = (key: string): string[] => key.match(WORD_PATTERN) ?? [];

const camelCase = (key: string): string =>
  splitWords(key)
    .map((word, index) =>
      index === 0
        ? word.toLowerCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join("");

const snakeCase = (key: string): string =>
  splitWords(key)
    .map(word => word.toLowerCase())
    .join("_");

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (typeof value !== "object" || value === null) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === null || proto === Object.prototype;
};

const deepMapKeys = (
  object: Record<string, unknown>,
  renameKey: (key: string) => string,
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(object).map(([key, value]) => [
      renameKey(key),
      isPlainObject(value)
        ? deepMapKeys(value, renameKey)
        : Array.isArray(value)
          ? value.map(item =>
              isPlainObject(item) ? deepMapKeys(item, renameKey) : item,
            )
          : value,
    ]),
  );

export const mapKeysToCamelCase = (
  data: Record<string, unknown> | Array<Record<string, unknown>>,
): Record<string, unknown> | Array<Record<string, unknown>> =>
  Array.isArray(data)
    ? data.map(obj => deepMapKeys(obj, camelCase))
    : deepMapKeys(data, camelCase);

export const mapKeysToSnakeCase = (
  data: Record<string, unknown> | Array<Record<string, unknown>>,
): Record<string, unknown> | Array<Record<string, unknown>> =>
  Array.isArray(data)
    ? data.map(obj => deepMapKeys(obj, snakeCase))
    : deepMapKeys(data, snakeCase);

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- domain interfaces lack an index signature
type RequestPayload = Record<string, any>;

interface RequestOptions {
  signal?: AbortSignal;

  envVar?: string;

  responseType?: ResponseType;
  withCredentials?: boolean;
  withXSRFToken?: boolean;
  publicResource?: boolean;

  mapToCamelCase?: boolean;

  skipBodySnakeCase?: boolean;

  onUploadProgress?: (progress: number) => void;
}

export async function getDataAPIRaw(
  url: string,
  method: APIMethods = APIMethods.GET,
  data: RequestPayload = {},
  queryParams: RequestPayload = {},
  headers: Record<string, string> = { "Content-Type": "application/json" },
  options: RequestOptions = {},
) {
  const {
    signal,
    envVar = API_URL,
    responseType,
    withCredentials,
    withXSRFToken,
    onUploadProgress,
  } = options;
  return await axios({
    method,
    url: `${envVar !== "" ? API_URL : ""}${url}`,
    data:
      data instanceof FormData || options.skipBodySnakeCase
        ? data
        : mapKeysToSnakeCase(data),
    params:
      queryParams instanceof URLSearchParams
        ? queryParams
        : mapKeysToSnakeCase(queryParams),
    headers: { ...headers, "Alloweat-Date": new Date().toUTCString() },
    signal,
    responseType,
    withCredentials,
    withXSRFToken,
    onUploadProgress: onUploadProgress
      ? event => onUploadProgress(event.progress ?? 0)
      : undefined,
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- retained default for existing untyped API clients
export async function getDataAPI<T = any>(
  url: string,
  method: APIMethods = APIMethods.GET,
  data: RequestPayload = {},
  queryParams: RequestPayload = {},
  headers: Record<string, string> = { "Content-Type": "application/json" },
  options: RequestOptions = {},
): Promise<T> {
  if (options.publicResource) {
    if (
      method !== APIMethods.GET ||
      !["blob", "json"].includes(options.responseType ?? "")
    ) {
      throw new Error("Public resources require a binary or JSON GET request");
    }
    const response = await fetch(url, {
      credentials: "omit",
      signal: options.signal,
    });
    if (!response.ok)
      throw new Error(`Image request failed (${response.status})`);
    if (options.responseType === "blob") return (await response.blob()) as T;
    const result: unknown = await response.json();
    if (options.mapToCamelCase === false) return result as T;
    if (isPlainObject(result)) return mapKeysToCamelCase(result) as T;
    if (Array.isArray(result))
      return result.map(item =>
        isPlainObject(item) ? mapKeysToCamelCase(item) : item,
      ) as T;
    return result as T;
  }
  const response = await getDataAPIRaw(
    url,
    method,
    data,
    queryParams,
    headers,
    options,
  );

  const mapToCamelCase = options.mapToCamelCase ?? true;
  return (
    mapToCamelCase ? mapKeysToCamelCase(response.data ?? {}) : response.data
  ) as T;
}

export async function getDataAPI2(
  url: string,
  method: APIMethods = APIMethods.GET,
  data: RequestPayload = {},
  queryParams: RequestPayload = {},
  headers: Record<string, string> = { "Content-Type": "application/json" },
  options: RequestOptions = {},
): Promise<AxiosResponse> {
  return await getDataAPIRaw(url, method, data, queryParams, headers, options);
}
